import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useSearchParams,
} from "react-router-dom";
import { ThemeProvider, BaseStyles } from "@primer/react";

import "@primer/primitives/dist/css/functional/themes/light.css";
import "@primer/primitives/dist/css/functional/themes/dark.css";

import Home from "./components/Home/index";
import {
  ThemeContext,
  getSystemPreference,
  resolveInitialMode,
  resolveMode,
  storeMode,
} from "./themeContext";

// The iframe host passes ?theme and always wins; standalone visitors resume the
// mode they last toggled to. Either way the param only ever seeds the state,
// and embedded runs neither read nor write the stored preference. A standalone
// first visit follows the OS until the visitor picks a side.
function ThemedRoutes() {
  const [searchParams] = useSearchParams();
  const paramTheme = searchParams.get("theme");
  const isIframe = searchParams.get("iframe") === "true";
  const [mode, setMode] = useState(() =>
    resolveInitialMode(paramTheme, isIframe)
  );
  const [systemPreference, setSystemPreference] = useState(getSystemPreference);
  const resolvedMode = resolveMode(mode, systemPreference);

  // The host re-renders the iframe src when it flips its own theme. Only react
  // when the param is actually present, so a standalone toggle isn't clobbered.
  useEffect(() => {
    if (paramTheme === "dark" || paramTheme === "light") setMode(paramTheme);
  }, [paramTheme]);

  // Only matters while mode is "auto", but the listener is cheap and keeping it
  // unconditional avoids re-subscribing on every mode change.
  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mediaQuery?.addEventListener) return;
    const handler = (e) => setSystemPreference(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Two-state: flip whatever is on screen. "auto" is only ever the pre-toggle
  // state, so the first click resolves it to an explicit pick and it never
  // returns. Embedded runs never persist that pick.
  const toggleMode = useCallback(() => {
    const next = resolvedMode === "dark" ? "light" : "dark";
    if (!isIframe) storeMode(next);
    setMode(next);
  }, [resolvedMode, isIframe]);

  const theme = useMemo(
    () => ({
      mode,
      resolvedMode,
      isDark: resolvedMode === "dark",
      toggleMode,
    }),
    [mode, resolvedMode, toggleMode]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-color-mode", resolvedMode);
    // Primer's primitives CSS keys on data-color-mode + data-{light|dark}-theme,
    // both required for the token block to resolve at :root.
    root.setAttribute("data-light-theme", "light");
    root.setAttribute("data-dark-theme", "dark");
  }, [resolvedMode]);

  return (
    <ThemeContext.Provider value={theme}>
      <ThemeProvider colorMode={resolvedMode}>
        <BaseStyles
          fontFamily="SF Pro Text, sans-serif"
          color="var(--fgColor-default)"
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/:slug" element={<Home />} />
          </Routes>
        </BaseStyles>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <ThemedRoutes />
    </Router>
  );
}

export default App;
